import {
  Avatar, Box, Divider, IconButton, Menu, MenuItem,
  ListItemIcon, Tab, Tabs, Tooltip, Typography,
  useMediaQuery, AppBar, Toolbar, Drawer, List, ListItemButton, ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';

const SIDEBAR_WIDTH = 256;

export type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  exact?: boolean;
};

export type NavSection = {
  heading?: string;
  items: NavItem[];
};

export function AppShell({
  brand,
  sections,
  noSidebar = false,
  noChrome = false,
  noHeader = false,
}: {
  brand: { title: string; subtitle?: string; href: string };
  sections: NavSection[];
  noSidebar?: boolean;
  noChrome?: boolean;
  noHeader?: boolean;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Flatten all items from all sections for the tabs
  const allItems = sections.flatMap((s) => s.items);

  // Find active tab index
  const activeIndex = allItems.findIndex((item) =>
    item.exact
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to)
  );

  // --- Tab nav bar (shown when noHeader is true, replaces sidebar) ---
  const tabNav = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        height: 56,
      }}
    >
      {/* Brand */}
      <Typography
        component={RouterLink}
        to={brand.href}
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          mr: 3,
          display: { xs: 'none', md: 'block' },
        }}
      >
        {brand.subtitle ?? brand.title}
      </Typography>

      {/* Mobile: hamburger */}
      <IconButton
        edge="start"
        onClick={() => setDrawerOpen(true)}
        sx={{ mr: 1, display: { md: 'none' } }}
        aria-label="open navigation"
      >
        <MenuIcon />
      </IconButton>

      {/* Tabs — desktop */}
      <Tabs
        value={activeIndex === -1 ? false : activeIndex}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexGrow: 1,
          display: { xs: 'none', md: 'flex' },
          minHeight: 56,
          '& .MuiTab-root': { minHeight: 56, minWidth: 'unset', px: 2 },
        }}
      >
        {allItems.map((item) => (
          <Tab
            key={item.to}
            component={RouterLink}
            to={item.to}
            label={item.label}
            icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>{item.icon}</Box>}
            iconPosition="start"
            sx={{ textTransform: 'none', fontSize: '0.9rem' }}
          />
        ))}
      </Tabs>

      <Box sx={{ flexGrow: 1 }} />
      <UserMenu />
    </Box>
  );

  // --- Drawer content (mobile fallback for all modes) ---
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography
          variant="h6"
          component={RouterLink}
          to={brand.href}
          sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 700 }}
        >
          {brand.title}
        </Typography>
        {brand.subtitle && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {brand.subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1.5 }}>
        {sections.map((section, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            {section.heading && (
              <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', display: 'block' }}>
                {section.heading}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item) => {
                const active = item.exact
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to);
                return (
                  <ListItemButton
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    selected={active}
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      mx: 1, borderRadius: 1.5,
                      '&.Mui-selected': {
                        bgcolor: (t) => t.palette.action.selected,
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                        '& .MuiListItemText-primary': { fontWeight: 600, color: 'primary.main' },
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 36, display: 'flex', alignItems: 'center' }}>
                      {item.icon}
                    </Box>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.9375rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // ── noHeader mode ── tabs on top, no sidebar
  if (noHeader && !noChrome) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {tabNav}

        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: '56px',
            bgcolor: 'background.default',
            p: { xs: 2, md: 4 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    );
  }

  // ── noChrome mode ── no header, no sidebar, bare content
  if (noChrome) {
    return (
      <Box
        component="main"
        sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}
      >
        <Outlet />
      </Box>
    );
  }

  // ── Default mode ── classic AppBar + sidebar
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: noSidebar ? '100%' : `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { md: noSidebar ? 0 : `${SIDEBAR_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 2, display: noSidebar ? 'none' : { md: 'none' } }}
            aria-label="open navigation"
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            component={RouterLink}
            to={brand.href}
            sx={{
              display: { xs: 'block', md: 'none' },
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 700,
              flexGrow: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {brand.subtitle ?? brand.title}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: noSidebar ? 'none' : { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: noSidebar ? '100%' : { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

function UserMenu() {
  const { user } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user || !user.is_authenticated) return null;

  const initials = (user.email || '?')
    .split('@')[0]!
    .split(/[._-]/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);

  return (
    <>
      <Tooltip title={user.email}>
        <IconButton onClick={(e) => setAnchor(e.currentTarget)}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
            {initials || '?'}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="subtitle2">{user.email}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user.user_type ?? 'user'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => window.location.assign('/auth/logout/')}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}